import type { OpenPencilSourceWorkspace } from "./openpencil-source-workspace";

export interface ExternalFigmaUrlReference {
  fileKey: string;
  nodeId: string;
  url: string;
  observedAt: string;
  version?: string;
  hash?: string;
}

export interface ExternalDesignSyncBackEvidence {
  sourceId: string;
  sourceRevision: number;
  sourceSha256: string;
  fileKey: string;
  upstreamVersion: string;
  syncedAt: string;
}

export type ExternalDesignSourceStateName =
  | "disconnected"
  | "reference-only"
  | "editable-local-revision"
  | "local-revision-not-synced"
  | "candidate-bound";

export type ExternalDesignSourceLabel =
  | "Imported from Figma"
  | "Editing local revision"
  | "Not synced to Figma"
  | "Synced as new Figma revision";

export type ExternalDesignSourceBlocker =
  | "figma-reference-missing"
  | "editable-local-revision-missing"
  | "figma-sync-back-pending"
  | "figma-sync-back-evidence-mismatch"
  | "source-candidate-bound";

export interface ExternalDesignSourceProvenance {
  sourceId: string | null;
  sourceRevision: number | null;
  sha256: string | null;
  upstream: {
    fileKey: string | null;
    nodeId: string | null;
    url: string | null;
    observedAt: string | null;
    version: string | null;
    hash: string | null;
    syncedVersion: string | null;
  };
}

export interface ExternalDesignSourceState {
  state: ExternalDesignSourceStateName;
  labels: ExternalDesignSourceLabel[];
  blockers: ExternalDesignSourceBlocker[];
  /** Standalone source editing is blocked after a candidate is bound. */
  editable: boolean;
  /** A bound candidate may still use the approved source+Cogo coordinator. */
  coordinatedMutationSourceReady: boolean;
  syncBackVerified: boolean;
  provenance: ExternalDesignSourceProvenance;
}

export interface DeriveExternalDesignSourceStateInput {
  figmaReference?: ExternalFigmaUrlReference | null;
  workspace?: OpenPencilSourceWorkspace | null;
  syncBack?: ExternalDesignSyncBackEvidence | null;
}

const SHA256_PATTERN = /^[a-f0-9]{64}$/;

function hasText(value: string | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasFigmaReference(
  reference: ExternalFigmaUrlReference | null | undefined,
): reference is ExternalFigmaUrlReference {
  return Boolean(
    reference &&
    hasText(reference.fileKey) &&
    hasText(reference.nodeId) &&
    hasText(reference.url) &&
    hasText(reference.observedAt),
  );
}

function hasEditableLocalRevision(
  workspace: OpenPencilSourceWorkspace | null | undefined,
): workspace is OpenPencilSourceWorkspace {
  return Boolean(
    workspace &&
    workspace.bundle.status === "ready" &&
    hasText(workspace.bundle.sourceArtifactRef) &&
    hasText(workspace.bundle.fileSha256) &&
    SHA256_PATTERN.test(workspace.bundle.fileSha256),
  );
}

function hasExplicitSyncBackEvidence(
  evidence: ExternalDesignSyncBackEvidence | null | undefined,
): evidence is ExternalDesignSyncBackEvidence {
  return Boolean(
    evidence &&
      hasText(evidence.sourceId) &&
      Number.isInteger(evidence.sourceRevision) &&
      evidence.sourceRevision > 0 &&
      hasText(evidence.sourceSha256) &&
      SHA256_PATTERN.test(evidence.sourceSha256) &&
      hasText(evidence.fileKey) &&
      hasText(evidence.upstreamVersion) &&
    hasText(evidence.syncedAt),
  );
}

export function deriveExternalDesignSourceState(
  input: DeriveExternalDesignSourceStateInput = {},
): ExternalDesignSourceState {
  const reference = hasFigmaReference(input.figmaReference)
    ? input.figmaReference
    : null;
  const workspace = input.workspace ?? null;
  const editableWorkspace = hasEditableLocalRevision(workspace)
    ? workspace
    : null;
  const lineage = editableWorkspace?.bundle.revisionLineage ?? null;
  const sourceRevision = workspace
    ? (workspace.bundle.revisionLineage?.sourceRevision ?? 0)
    : null;
  const explicitSyncBack = hasExplicitSyncBackEvidence(input.syncBack)
    ? input.syncBack
    : null;
  const syncBackVerified = Boolean(
    reference &&
      lineage &&
      explicitSyncBack &&
      explicitSyncBack.sourceId === editableWorkspace?.sourceId &&
      explicitSyncBack.sourceRevision === lineage.sourceRevision &&
      explicitSyncBack.sourceSha256 === editableWorkspace.bundle.fileSha256 &&
      explicitSyncBack.fileKey === reference.fileKey,
  );
  const hasUnsyncedLocalRevision = Boolean(lineage && !syncBackVerified);
  const blockers: ExternalDesignSourceBlocker[] = [];

  if (!reference && !editableWorkspace) {
    blockers.push("figma-reference-missing");
  }
  if (!editableWorkspace) {
    blockers.push("editable-local-revision-missing");
  }
  if (lineage && !reference) {
    blockers.push("figma-reference-missing");
  }
  if (hasUnsyncedLocalRevision) {
    blockers.push(
      input.syncBack
        ? "figma-sync-back-evidence-mismatch"
        : "figma-sync-back-pending",
    );
  } else if (input.syncBack && !syncBackVerified) {
    blockers.push("figma-sync-back-evidence-mismatch");
  }
  if (editableWorkspace?.candidateBinding) {
    blockers.push("source-candidate-bound");
  }

  const state: ExternalDesignSourceStateName = !editableWorkspace
    ? reference
      ? "reference-only"
      : "disconnected"
    : editableWorkspace.candidateBinding
      ? "candidate-bound"
      : hasUnsyncedLocalRevision
        ? "local-revision-not-synced"
        : "editable-local-revision";
  const labels: ExternalDesignSourceLabel[] = [];

  if (reference) {
    labels.push("Imported from Figma");
  }
  if (editableWorkspace) {
    labels.push("Editing local revision");
  }
  if (hasUnsyncedLocalRevision) {
    labels.push("Not synced to Figma");
  } else if (syncBackVerified) {
    labels.push("Synced as new Figma revision");
  }

  return {
    state,
    labels,
    blockers: [...new Set(blockers)],
    editable: Boolean(editableWorkspace && !editableWorkspace.candidateBinding),
    coordinatedMutationSourceReady: Boolean(editableWorkspace),
    syncBackVerified,
    provenance: {
      sourceId: workspace?.sourceId ?? null,
      sourceRevision,
      sha256: workspace?.bundle.fileSha256 ?? null,
      upstream: {
        fileKey: reference?.fileKey ?? null,
        nodeId: reference?.nodeId ?? null,
        url: reference?.url ?? null,
        observedAt: reference?.observedAt ?? null,
        version: reference?.version ?? null,
        hash: reference?.hash ?? null,
        syncedVersion: syncBackVerified
          ? (explicitSyncBack?.upstreamVersion ?? null)
          : null,
      },
    },
  };
}
